import React, { useState, useEffect } from 'react';
import { TabbedForm, useDataProvider, useLocaleState, useTranslate } from 'react-admin';
import { EventFormToolbar } from './event-utils/EventFormToolbar';
import { EventFormHeader } from './event-utils/EventFormHeader';
import { LazyFormTab } from './event-utils/LazyFormTab';
import { EventTabNavProvider, EventFormTabs } from './event-utils/EventTabNav';
import { EventBasicDetailsTab } from './event-tabs/EventBasicDetailsTab';
import { EventDateDetailsTab } from './event-tabs/EventDateDetailsTab';
import { EventMoreDetailsTab } from './event-tabs/EventMoreDetailsTab';
import { EventVisualsTab } from './event-tabs/EventVisualsTab';
import { EventProductsTab } from './event-tabs/EventProductsTab';
import { EventStockEditTab } from './event-tabs/EventStockEditTab';
import { EventLayoutTab } from './event-tabs/EventLayoutTab';
import { EventCouponsTab } from './event-tabs/EventCouponsTab';
import { EventFeesTab } from './event-tabs/EventFeesTab';
import { EventSettingsTab } from './event-tabs/EventSettingsTab';
import { normalizeEventType, shouldShowLayoutTab } from '../../utils/eventFormUtils';

/**
 * Event V2 Form Component
 * Shared form component for both create and edit operations
 */
export const EventV2Form = ({
  isEdit = false,
  permissions = {},
  record = {},
  cloneRecord = null,
  eventType: initialEventType,
  dateType: initialDateType,
}) => {
  const dataProvider = useDataProvider();
  const [eventType, setEventType] = useState(initialEventType);
  const [dateType, setDateType] = useState(initialDateType);
  const [systemParams, setSystemParams] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch system parameters
  useEffect(() => {
    const fetchSystemParams = async () => {
      try {
        const res = await dataProvider.getOne('systemParameters', { id: 'system' });
        setSystemParams(res.data);
      } catch (error) {
        console.error('Error fetching system parameters:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSystemParams();
  }, [dataProvider]);

  const handleEventTypeChange = React.useCallback((newEventType) => {
    setEventType(normalizeEventType(newEventType));
  }, []);

  const handleDateTypeChange = React.useCallback((newDateType) => {
    setDateType(newDateType);
  }, []);

  const formProps = React.useMemo(
    () => ({
      isEdit,
      permissions,
      record,
      eventType,
      dateType,
      onEventTypeChange: handleEventTypeChange,
      onDateTypeChange: handleDateTypeChange,
      systemParams,
    }),
    [
      isEdit,
      permissions,
      record,
      eventType,
      dateType,
      handleEventTypeChange,
      handleDateTypeChange,
      systemParams,
    ],
  );

  const formDefaultValues = React.useMemo(() => {
    if (isEdit && record) {
      // Edit: record'daki dateType/type formda kesin olsun
      return {
        ...record,
        type: record.type ?? eventType,
        dateType: record.dateType ?? dateType ?? 'oneoff',
      };
    }
    if (cloneRecord) {
      return {
        ...cloneRecord,
        type: cloneRecord.type ?? eventType,
        dateType: cloneRecord.dateType ?? dateType,
      };
    }
    if (eventType && dateType) {
      return { type: eventType, dateType };
    }
    return undefined;
  }, [isEdit, record, cloneRecord, eventType, dateType]);

  const translate = useTranslate();
  const [locale] = useLocaleState();

  const transform = React.useCallback(
    (data) => ({
      ...data,
      sourceLang: isEdit ? record?.sourceLanguage || 'tr' : locale || 'tr',
    }),
    [isEdit, record?.sourceLanguage, locale],
  );

  if (loading) {
    return <div>{translate('ra.page.loading', { _: 'Yükleniyor...' })}</div>;
  }

  // Basic, Date, More, Visuals, Products, [Layout?], Coupons, Fees, Settings
  // Build as a dense array — `{cond && <Tab>}` leaves a hole in React.Children
  // indices, so Next lands on a blank slot between Products and Coupons.
  const showLayout = shouldShowLayoutTab(eventType);
  const formTabs = [
    <LazyFormTab key="basic" label={translate('form.basic_details', 'Basic Details')}>
      <EventBasicDetailsTab {...formProps} />
    </LazyFormTab>,
    <LazyFormTab key="date" label={translate('form.date_details', 'Date Details')}>
      <EventDateDetailsTab {...formProps} />
    </LazyFormTab>,
    <LazyFormTab key="more" label={translate('form.more_details', 'Description & Rules')}>
      <EventMoreDetailsTab {...formProps} />
    </LazyFormTab>,
    <LazyFormTab key="visuals" label={translate('form.visuals', 'Visuals')}>
      <EventVisualsTab {...formProps} />
    </LazyFormTab>,
    <LazyFormTab key="products" label={translate('form.products', 'Products & Stocks')}>
      {isEdit ? <EventStockEditTab {...formProps} /> : <EventProductsTab {...formProps} />}
    </LazyFormTab>,
    ...(showLayout
      ? [
          <LazyFormTab key="layout" label={translate('form.layout', 'Layout')}>
            <EventLayoutTab {...formProps} />
          </LazyFormTab>,
        ]
      : []),
    <LazyFormTab key="coupons" label={translate('form.coupons', 'Coupons')}>
      <EventCouponsTab {...formProps} />
    </LazyFormTab>,
    <LazyFormTab key="fees" label={translate('form.fees', 'Fees & Commissions')}>
      <EventFeesTab {...formProps} />
    </LazyFormTab>,
    <LazyFormTab key="settings" label={translate('form.settings', 'Settings & Status')}>
      <EventSettingsTab {...formProps} />
    </LazyFormTab>,
  ];
  const tabCount = formTabs.length;

  return (
    <EventTabNavProvider tabCount={tabCount}>
      <EventFormHeader
        eventType={eventType}
        dateType={dateType}
        eventName={cloneRecord?.name ?? record?.name}
        isEdit={isEdit}
        isCloned={!!cloneRecord?.isCloned}
        record={record}
      />

      {cloneRecord?.isCloned && (
        <div style={{ margin: '0 15px 16px', color: '#b45309', fontWeight: 500 }}>
          Cloned event — dates are empty; set new start/end dates before publishing. Name, venue,
          tickets and coupons were copied from the source event.
        </div>
      )}

      <TabbedForm
        warnWhenUnsavedChanges
        shouldUnregister={false}
        syncWithLocation={false}
        id="eventForm"
        transform={transform}
        defaultValues={formDefaultValues}
        tabs={<EventFormTabs />}
        toolbar={<EventFormToolbar isEdit={isEdit} eventType={eventType} dateType={dateType} />}
      >
        {formTabs}
      </TabbedForm>
    </EventTabNavProvider>
  );
};
